import { cancellableSleep, SleepResolutionType } from '@canvas/sleep';
export type { Timeout, SleepResolutionType } from '@canvas/sleep';

export enum PageVisbilityTransitionResolutionType {
  STATE_CHANGE = 'state-change',
  CANCELLED = 'cancelled',
}

export type WaitForPageVisibilityTransitionOutput = {
  promise: Promise<PageVisbilityTransitionResolutionType>;
  cancel: () => void;
};

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
 */
export const isPageHidden = () =>
  typeof globalThis.document !== 'undefined'
    ? globalThis.document.visibilityState === 'hidden'
    : false;

export const userCanSeePage = () => !isPageHidden();

/**
 * Resolves if {@link isStateMet} evaluates to true when the page visibility state changes.
 * @param isStateMet The definition for whether the page visibity state has been met. See {@link isPageHidden} and {@link userCanSeePage}
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
 */
export const waitForPageVisibilityTransition = (
  isStateMet: () => boolean,
): WaitForPageVisibilityTransitionOutput => {
  if (typeof globalThis.document === 'undefined') {
    return {
      promise: Promise.resolve(PageVisbilityTransitionResolutionType.STATE_CHANGE),
      cancel: () => {},
    };
  }

  let promiseResolve: (resolutionType: PageVisbilityTransitionResolutionType) => void =
    undefined as any;

  const promise = new Promise<PageVisbilityTransitionResolutionType>((resolve) => {
    promiseResolve = resolve;
  });

  const checkIfVisible = () => {
    if (isStateMet()) {
      globalThis.document.removeEventListener('visibilitychange', checkIfVisible);
      promiseResolve(PageVisbilityTransitionResolutionType.STATE_CHANGE);
    }
  };

  globalThis.document.addEventListener('visibilitychange', checkIfVisible);

  const cancel = () => {
    globalThis.document.removeEventListener('visibilitychange', checkIfVisible);
    promiseResolve(PageVisbilityTransitionResolutionType.CANCELLED);
  };

  return {
    promise,
    cancel,
  };
};

export enum WaitForPageVisibilityType {
  IMMEDIATE = 'immediate',
  WAITING = 'waiting',
}

export type PageVisibilityWaiting = {
  type: WaitForPageVisibilityType.WAITING;
  wait: WaitForPageVisibilityTransitionOutput;
};

export type PageVisibilityImmediate = {
  type: WaitForPageVisibilityType.IMMEDIATE;
  wait: undefined;
};

export type WaitForPageVisibilityOutput = PageVisibilityWaiting | PageVisibilityImmediate;

/**
 * Resolves a promise immeditely if {@link isStateMet} evaluates to true but others defers to {@link waitForPageVisibilityTransition}
 *
 * @returns The waiter promise and a cancel function
 */
export const waitForPageVisibility = (
  isStateMet: () => boolean,
): WaitForPageVisibilityOutput => {
  const pageWait = waitForPageVisibilityTransition(isStateMet);

  if (isStateMet()) {
    pageWait.cancel();

    return {
      type: WaitForPageVisibilityType.IMMEDIATE,
      wait: undefined,
    };
  }

  return {
    type: WaitForPageVisibilityType.WAITING,
    wait: pageWait,
  };
};

export const DEFAULT_INACTIVE_TIMEOUT = 5 * 60 * 1000;
export const defaultPageInactiveTimeout = () =>
  cancellableSleep(DEFAULT_INACTIVE_TIMEOUT);

export const defaultWaitForPageHidden = () =>
  waitForPageVisibilityTransition(isPageHidden);

export enum UserInactiveResolutionType {
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
}

/**
 * Waits for the page to become hidden (see: {@link waitForPageHidden}) from the user after which an inactivity timeout starts (see: {@link waitForInactiveTimeout}).
 * The function's promise resolves once the inactivity timeout is complete or you cancelled the waiter.
 *
 * @returns The waiter promise and cancel function
 */
export const waitForUserInactive = (
  waitForPageHidden = defaultWaitForPageHidden,
  waitForInactiveTimeout = defaultPageInactiveTimeout,
) => {
  const pageHiddenWaiter = waitForPageHidden();

  let currentCancel = () => pageHiddenWaiter.cancel();

  const promise = pageHiddenWaiter.promise.then(async (visibilityResolutionType) => {
    if (visibilityResolutionType === PageVisbilityTransitionResolutionType.CANCELLED) {
      return UserInactiveResolutionType.CANCELLED;
    }

    const sleeper = waitForInactiveTimeout();

    currentCancel = () => sleeper.cancel();

    const sleepResolutionType = await sleeper.promise;

    if (sleepResolutionType === SleepResolutionType.CANCELLED) {
      return UserInactiveResolutionType.CANCELLED;
    }

    return UserInactiveResolutionType.COMPLETE;
  });

  const cancel = () => currentCancel();

  return {
    promise,
    cancel,
  };
};
